import { reportError } from '../../../services/ErrorReporting';
import livestreamActions from '../../../actions/LivestreamActions';
import storeManager from '../../../store/StoreManager';
import utilService from '../../../services/UtilService';
import settingsReader from '../../../store/readHelpers/SettingsReader';
import sessionReader from '../../../store/readHelpers/SessionReader';
import forensicLogger from '../../../services/livesession/ForensicLogger';

const URL_PREFIX = 'ws://localhost:9321/media/';
const WEBCAM_URL = `${URL_PREFIX}webcam`;
const MIC_URL = `${URL_PREFIX}mic`;
const CAMERA_INITIALIZATION_DELAY_MS = 100;
let WEBCAM_FPS = 1;
/**
 * source file - AudioBufferCapture.js
 * run "yarn encode:audio-worklet-module" do generate encoded module string
 */
/* eslint-disable max-len */
const AUDIO_WORKLET_MODULE_URL = 'data:application/javascript;base64,ZnVuY3Rpb24gY29uY2F0KGEsIGIpIHsNCiAgICBjb25zdCB0bXAgPSBuZXcgVWludDhBcnJheShhLmJ5dGVMZW5ndGggKyBiLmJ5dGVMZW5ndGgpOw0KICAgIHRtcC5zZXQobmV3IFVpbnQ4QXJyYXkoYSksIDApOw0KICAgIHRtcC5zZXQobmV3IFVpbnQ4QXJyYXkoYiksIGEuYnl0ZUxlbmd0aCk7DQogICAgcmV0dXJuIHRtcC5idWZmZXI7DQp9DQoNCmNsYXNzIEF1ZGlvQnVmZmVyQ2FwdHVyZSBleHRlbmRzIEF1ZGlvV29ya2xldFByb2Nlc3NvciB7DQogICAgY29uc3RydWN0b3IoKSB7DQogICAgICAgIHN1cGVyKCk7DQogICAgICAgIHRoaXMuYnVmID0gbmV3IEFycmF5QnVmZmVyKDApOw0KICAgIH0NCg0KICAgIHByb2Nlc3MoaW5wdXRzKSB7DQogICAgICAgIGNvbnN0IFdFQkNBTV9GUFMgPSAxOw0KICAgICAgICBjb25zdCBNSUNfU0FNUExFX1JBVEUgPSAxNjAwMDsNCiAgICAgICAgY29uc3QgTUlDX0JVRkZFUl9TSVpFID0gKCg2MDAgKiBNSUNfU0FNUExFX1JBVEUpIC8gMTAwMDApICogNDsNCg0KICAgICAgICAvLyBGb3IgdW5rbm93biByZWFzb24gdGhlIHdlYmNhbSBzdHJlYW0gaXMgZGVsYXllZCBieSAxIGZyYW1lIGluIHJlbGF0aW9uDQogICAgICAgIC8vIHRvIHRoZSBtaWMgc3RyZWFtLCBzbyB3ZSBkZWxpYmVyYXRlbHkgZGVsYXkgYXVkaW8gc3RyZWFtIGJ5IHRoaXMNCiAgICAgICAgLy8gZHVyYXRpb24uDQogICAgICAgIGNvbnN0IHNlbmRTaXplVGhyZXNob2xkID0gKE1JQ19TQU1QTEVfUkFURSAvIFdFQkNBTV9GUFMpICsgTUlDX0JVRkZFUl9TSVpFOw0KDQogICAgICAgIGNvbnN0IHNhbXBsZXNCdWZmZXIgPSBpbnB1dHNbMF0/LlswXT8uYnVmZmVyOw0KDQogICAgICAgIGlmIChzYW1wbGVzQnVmZmVyKSB7DQogICAgICAgICAgICB0aGlzLmJ1ZiA9IGNvbmNhdCh0aGlzLmJ1Ziwgc2FtcGxlc0J1ZmZlcik7DQogICAgICAgIH0NCg0KICAgICAgICB3aGlsZSAodGhpcy5idWYuYnl0ZUxlbmd0aCA+PSBzZW5kU2l6ZVRocmVzaG9sZCkgew0KICAgICAgICAgICAgdGhpcy5wb3J0LnBvc3RNZXNzYWdlKHRoaXMuYnVmLnNsaWNlKDAsIE1JQ19CVUZGRVJfU0laRSkpOw0KICAgICAgICAgICAgdGhpcy5idWYgPSB0aGlzLmJ1Zi5zbGljZShNSUNfQlVGRkVSX1NJWkUpOw0KICAgICAgICB9DQoNCiAgICAgICAgcmV0dXJuIHRydWU7DQogICAgfQ0KfQ0KDQpyZWdpc3RlclByb2Nlc3NvcignQXVkaW9CdWZmZXJDYXB0dXJlJywgQXVkaW9CdWZmZXJDYXB0dXJlKTsNCg==';
const AUDIO_ERROR_CHECKING_INTERVAL = 15000;

const isSocketDead = socket => socket !== null && socket.readyState > WebSocket.OPEN;

let audioRecorder;

const recordManagerTimePoints = {
    recordManagerStartTime: null,
    audioStartTime: null,
    webcamStartTime: null,
    latestWorkingAudioTime: null,
};

const sendMessageIfSocketOpen = (socket, message) => {
    if (socket !== null && socket.readyState === WebSocket.OPEN) {
        socket.send(message.data || message);
    }
};

class ReceivedStreamCommand {
    constructor(recorder) {
        this.recorder = recorder;
    }

    run(receivedEvent) {
        this.recorder.startRemoteStream(receivedEvent.data);
    }
}

class WebcamRecord {
    constructor(config, stream) {
        this.video = document.getElementById('webcam_livestream');
        this.canvas = document.getElementById('canvas_webcam_livestream');
        this.canvasCtx = this.canvas.getContext('2d');

        const constraints = config.constraints.video();
        this.video.width = constraints.width;
        this.video.height = constraints.height;
        this.canvas.width = constraints.width;
        this.canvas.height = constraints.height;
        this.canvas.hidden = true;
        this.video.srcObject = stream;
        this.video.volume = 0.001; // volume = 0 as well as 'mute' prevents video from rendering.

        this.socket = null;
    }

    update() {
        this.canvasCtx.drawImage(
            this.video,
            0,
            0,
            this.canvas.width,
            this.canvas.height
        );

        const data = this.canvasCtx.getImageData(
            0,
            0,
            this.canvas.width,
            this.canvas.height
        );

        const message = new Uint8Array(data.data).buffer;
        if (isSocketDead(this.socket)) {
            this.socket = new WebSocket(WEBCAM_URL);
            sendMessageIfSocketOpen(this.socket, message);
        } else {
            this.socket.send(message);
        }
    }

    /**
     * Starts the webcam capture process by setting up the interval timer
     * and initiating the first frame capture. Called after the WebSocket
     * connection is open and (when flag enabled) video is ready.
     */
    startWebcamCapture() {
        // Start the interval and first capture
        this.captureInterval = setInterval(this.update.bind(this), 1000 / WEBCAM_FPS);
        this.update();

        recordManagerTimePoints.webcamStartTime = Date.now();
        storeManager.dispatch(livestreamActions.setWebcamLivestreamAsStarted());
    }

    /**
     * Creates a promise that resolves when the video is ready
     * @returns {Promise<void>}
     */
    createVideoReadyPromise() {
        return new Promise((resolve, reject) => {
            // Check if video is already ready
            if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                resolve();
            } else {
                // Wait for video to have enough data or error
                const errorHandler = () => {
                    logError('Video failed to load - camera may be unavailable or permission denied');
                    this.video.removeEventListener('canplay', canplayHandler);
                    reject(new Error('Video failed to load'));
                };

                const canplayHandler = () => {
                    this.video.removeEventListener('error', errorHandler);
                    resolve();
                };

                this.video.addEventListener('error', errorHandler, { once: true });
                this.video.addEventListener('canplay', canplayHandler, { once: true });
            }
        });
    }

    /**
     * Handles socket open event when proctoring is enabled
     * Waits for video to be ready before starting capture
     */
    handleSocketOpenWithVideoReady() {
        const videoReadyPromise = this.createVideoReadyPromise();
        videoReadyPromise.then(() => {
            setTimeout(() => {
                this.startWebcamCapture();
            }, CAMERA_INITIALIZATION_DELAY_MS);
        }).catch((error) => {
            logError('Video initialization failed after socket opened', error);
        });
    }

    /**
     * Handles socket open event - branches based on proctoring flag
     */
    attachSocketOpenHandler() {
        this.socket.addEventListener('open', () => {
            if (sessionReader.isExitOnNegativeRemoteProctoringTimeoutEnabled()) {
                this.handleSocketOpenWithVideoReady();
            } else {
                // Original behavior when flag is disabled
                this.startWebcamCapture();
            }
        }, { once: true });
    }

    start() {
        WEBCAM_FPS = settingsReader?.getWebRTCRecordingWebCamFPS();
        this.socket = new WebSocket(WEBCAM_URL);

        // Log webcam record start for forensics (logs the video element srcObject stream)
        if (this.video.srcObject) {
            forensicLogger.logWebcamRecordStart(this.video.srcObject);
        }

        // Play the video element, logging errors if playback fails
        this.video.play().catch((err) => {
            logError('Video play() failed', err);
        });

        // Attach socket open handler that conditionally waits for video readiness
        // based on the proctoring timeout feature flag
        this.attachSocketOpenHandler();
    }
}

// Debug Live Proctoring Audio failures.
function logError(message, error = null) {
    const { livestream } = storeManager.getState();
    const timePoints = prepareReadableTimeLogs();
    const debugginData = {
        timePoints,
        livestreamStateDump: livestream,
    };

    if (error) {
        // Preserve Stack Trace and Error Type in TrackJS.
        console.error(message);
        reportError(error, debugginData);
    } else {
        reportError(message, debugginData);
    }
}

function prepareReadableTimeLogs() {
    const timePoints = {};

    try {
        const audioWorkingDuration = recordManagerTimePoints.latestWorkingAudioTime
            ? recordManagerTimePoints.latestWorkingAudioTime - recordManagerTimePoints.audioStartTime
            : 0;

        Object.keys(recordManagerTimePoints).forEach((key) => {
            const time = recordManagerTimePoints[key];
            timePoints[key] = time ? new Date(time) : null;
        });

        timePoints.workingAudioDuration = audioWorkingDuration
            ? utilService.getReadableDuration(audioWorkingDuration, true)
            : null;

        return timePoints;
    } catch (error) {
        return { ...recordManagerTimePoints };
    }
}

function monitorAudioCapture(currentAudioRecorder) {
    let lastReportedMessageTime;

    setInterval(() => {
        const maxDuration = AUDIO_ERROR_CHECKING_INTERVAL * 2;
        const { lastMessageTime } = currentAudioRecorder;

        // Ignore repeated errors.
        if (lastMessageTime === lastReportedMessageTime) {
            return;
        }

        const currentTime = Date.now();
        if (!lastMessageTime || (currentTime - lastMessageTime) > maxDuration) {
            lastReportedMessageTime = lastMessageTime;
            logError('No audio has been sent over socket since too long time.');
        }
    }, AUDIO_ERROR_CHECKING_INTERVAL);
}

class AudioRecord {
    constructor(config, streamManager) {
        this.config = config;
        this.streamManager = streamManager;
        this.liveAudioContext = null;
        this.recordWorklet = null;
        this.micStreamSource = null;
        this.socket = null;
        this.lastMessageTime = null;

        this.onRecordMessageBound = this.onRecordMessage.bind(this);
        this.startRecordingBound = this.startRecording.bind(this);
    }

    loadModule() {
        if (this.recordWorklet === null) {
            this.liveAudioContext.audioWorklet.addModule(AUDIO_WORKLET_MODULE_URL)
                .then(() => {
                    this.startWorklet();
                })
                .catch((error) => {
                    logError('Unable to load the AudioWorklet.', error);
                });
        } else {
            this.startWorklet();
        }
    }

    startWorklet() {
        if (this.recordWorklet === null) {
            this.recordWorklet = new AudioWorkletNode(
                this.liveAudioContext,
                'AudioBufferCapture'
            );

            this.recordWorklet.addEventListener(
                'processorerror',
                (processorErrorEvent) => {
                    logError('Record Worklet failed.', processorErrorEvent);
                },
                false
            );

            this.recordWorklet.port.start();

            this.recordWorklet.port.addEventListener(
                'message',
                this.onRecordMessageBound,
                false
            );
        }

        if (this.liveAudioContext.state === 'suspended') {
            this.liveAudioContext.resume().then(() => {
                this.waitWebSocket();
            }).catch((error) => {
                logError('AudioContext was suspended and could not be resumed.', error);
            });
        } else {
            this.waitWebSocket();
        }
    }

    waitWebSocket() {
        if (this.socket.readyState === WebSocket.OPEN) {
            this.startRecording();
        } else {
            /*
             * I can't use an arrow function because removeEventListener needs
             * the very same function.
             */
            this.socket.addEventListener(
                'open',
                this.startRecordingBound,
                false
            );
        }
    }

    startRecording() {
        this.socket.removeEventListener(
            'open',
            this.startRecordingBound,
            false
        );

        /*
         * If this function is ever called twice, prevent to double-connect the
         * mic to the Worklet.
         */
        if (this.micStreamSource) {
            return;
        }

        this.micStreamSource = this.liveAudioContext.createMediaStreamSource(
            this.streamManager.mirror()
        );

        this.micStreamSource.connect(this.recordWorklet);
        this.checkRemoteStream();
    }

    onRecordMessage(message) {
        const currentTime = Date.now();

        if (this.lastMessageTime === null) {
            recordManagerTimePoints.audioStartTime = Date.now();
            storeManager.dispatch(livestreamActions.setAudioLivestreamAsStarted());
        }

        this.lastMessageTime = currentTime;
        recordManagerTimePoints.latestWorkingAudioTime = currentTime;

        if (isSocketDead(this.socket)) {
            this.socket = new WebSocket(MIC_URL);
            sendMessageIfSocketOpen(this.socket, message);
        } else {
            sendMessageIfSocketOpen(this.socket, message);
        }
    }

    checkRemoteStream() {
        const remoteStream = this.streamManager.remote();

        if (remoteStream === null) {
            this.streamManager.onRemoteStream.listen(
                new ReceivedStreamCommand(this)
            );
        } else {
            this.startRemoteStream(remoteStream);
        }
    }

    startRemoteStream(remoteStream) {
        const remoteStreamSource = this.liveAudioContext.createMediaStreamSource(remoteStream);

        this.connectAudioStreamSourceToRecordWorklet(remoteStreamSource);
    }

    connectAudioStreamSourceToRecordWorklet(audioStreamSource) {
        audioStreamSource.connect(this.recordWorklet);
    }

    start() {
        monitorAudioCapture(this);

        const sampleRate = this.config.constraints.audio().sampleRate;
        this.liveAudioContext = new AudioContext({ sampleRate });
        this.socket = new WebSocket(MIC_URL);

        this.loadModule();
    }
}

class RecordManager {
    constructor(streamManager) {
        this.streamManager = streamManager;
    }

    start(config) {
        recordManagerTimePoints.recordManagerStartTime = Date.now();

        if (config.webcamStreaming) {
            const webcam = new WebcamRecord(config, this.streamManager.mirror());
            webcam.start();
        }

        if (config.audioStreaming) {
            audioRecorder = new AudioRecord(config, this.streamManager);
            audioRecorder.start();
        }
    }

    getLiveAudioContext() {
        return audioRecorder.liveAudioContext;
    }

    getAudioRecord() {
        return audioRecorder;
    }
}

export default RecordManager;
export { WebcamRecord };
